import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, Stethoscope, Handshake, Sparkles, Target, FileText, Radio } from 'lucide-react';
interface SlideDifferentiationProps {
  onContinue: () => void;
}
export function SlideDifferentiation({
  onContinue
}: SlideDifferentiationProps) {
  const [stage, setStage] = useState(0);
  const items = [{
    icon: <ShieldCheck className="w-5 h-5" data-id="element-169" />,
    title: 'Deep pharma marketing expertise',
    desc: 'Three decades operating inside regulated pharma sales and marketing — where generalist AI and broad agencies fail.'
  }, {
    icon: <Stethoscope className="w-5 h-5" data-id="element-170" />,
    title: '100% focused on Healthcare',
    desc: 'No side bets, no other verticals — every workflow, model and template is purpose-built for HCP audiences.'
  }, {
    icon: <Handshake className="w-5 h-5" data-id="element-171" />,
    title: 'Trusted by leading pharma',
    desc: 'Master agreements with 15 of the top 30 healthcare companies globally — already procurement-cleared.'
  }, {
    icon: <Sparkles className="w-5 h-5" data-id="element-172" />,
    title: 'Interactive engagement',
    desc: 'Every engagement is a lesson learned — behavioral signals continuously sharpen the next campaign.'
  }, {
    icon: <Target className="w-5 h-5" data-id="element-173" />,
    title: 'One-to-one precision',
    desc: "Tailored delivery to each HCP segment — not agencies' broad-stroke campaigns."
  }, {
    icon: <FileText className="w-5 h-5" data-id="element-174" />,
    title: 'Content agnostic',
    desc: 'We work with your existing approved assets — no rip-and-replace, no new content factory required.'
  }, {
    icon: <Radio className="w-5 h-5" data-id="element-175" />,
    title: 'Publishing agnostic',
    desc: 'Distribute everywhere HCPs are — email, banners, WhatsApp, SMS, Veeva, portals, sales reps, congresses. We integrate, you choose.'
  }, {
    icon: <Sparkles className="w-5 h-5" data-id="element-176" />,
    title: 'Actionable data layer',
    desc: 'Rich behavioral signals from polls, quizzes, rankings and interactions — use them standalone or stream them into any analytics platform (Veeva, Tableau, Power BI, your CDP). Data agnostic by design.'
  }, {
    icon: <Sparkles className="w-5 h-5" data-id="element-177" />,
    title: 'Focused AI × Pharma expertise × Compounding flywheel',
    desc: 'Purpose-built AI shaped by deep pharma knowledge, that gets sharper with every campaign. The combination is what no agency or generalist AI can replicate.'
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 250);
    const timers = items.map((_, idx) => setTimeout(() => setStage(2 + idx), 600 + idx * 180));
    const tEnd = setTimeout(() => setStage(2 + items.length), 600 + items.length * 180 + 200);
    return () => {
      clearTimeout(t1);
      timers.forEach(clearTimeout);
      clearTimeout(tEnd);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-6 md:px-16 md:py-8 max-w-7xl mx-auto overflow-y-auto" data-id="element-178">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-179">
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-180">
          What makes Snakker unique
        </h2>
        <p className="text-teal-400 text-base md:text-lg font-medium mt-1" data-id="element-181">
          Why we can actually do this — and others can't
        </p>
      </motion.div>

      {/* Items grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-8 flex-1 content-start" data-id="element-182">
        {items.map((item, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: stage >= 2 + idx ? 1 : 0,
        y: stage >= 2 + idx ? 0 : 16
      }} transition={{
        duration: 0.45
      }} className="bg-navy-900/60 border border-navy-600 rounded-2xl p-5 flex flex-col gap-3 hover:border-teal-400/40 transition-colors" data-id="element-183">
            <div className="w-10 h-10 rounded-xl bg-teal-400/10 border border-teal-400/30 flex items-center justify-center text-teal-400 flex-shrink-0" data-id="element-184">
              {item.icon}
            </div>
            <div data-id="element-185">
              <h3 className="text-base md:text-lg font-bold text-white leading-tight" data-id="element-186">
                {item.title}
              </h3>
              <p className="text-white/60 text-sm mt-1.5 leading-relaxed" data-id="element-187">
                {item.desc}
              </p>
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 + items.length ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-188">
        <Button onClick={onContinue} data-id="element-189">New Category</Button>
      </motion.div>
    </div>;
}